import { haversineDistance } from '../../../../javascript/utils.js';
import lithuaniaGeoJson from './lithuania_border.json';
import '/theme/libs/leaflet/plugins/BoundaryCanvas';
import '/theme/libs/leaflet/plugins/leaflet.conditionalLayer';
import '/theme/libs/leaflet/plugins/MarkerCluster/leaflet.markercluster';

function getMapMarkerIcon(container) {
	const marker = container.querySelector(
		'.locations-map-marker__wrapper .locations-map-marker svg'
	);
	if (!marker) return;
	const mapMarkerIcon = L.divIcon({
		html: marker.outerHTML,
		iconSize: [20, 31],
		iconAnchor: [10, 31],
		className: 'cancan-map-marker',
	});
	return mapMarkerIcon;
}

function getMapClusterIcon(container, count) {
	const svgEl = container.querySelector(
		'.locations-map-marker__wrapper .locations-map-cluster-marker svg'
	);
	if (!svgEl) return null;

	const clone = svgEl.cloneNode(true);
	const text = clone.querySelector('#count');
	if (text) text.textContent = count;

	return L.divIcon({
		html: clone.outerHTML,
		className: 'cancan-cluster-marker',
		iconSize: [20, 31],
		iconAnchor: [10, 31],
	});
}

async function fetchRestaurants() {
	const result = await fetch('/wp-json/cancan/v1/restaurants');

	if (!result.ok) {
		throw new Error('Cannot fetch restaurants');
	}

	return await result.json();
}

async function getRestaurants(loader) {
	if (loader) loader.classList.remove('hidden');

	try {
		const restaurants = await fetchRestaurants();
		return restaurants;
	} catch (error) {
		console.error(error);
		return [];
	} finally {
		if (loader) loader.classList.add('hidden');
	}
}

function initClusterMarkers(map, container, locations) {
	const ZOOM_AMOUNT = 3;
	const mapMarker = getMapMarkerIcon(container);
	const markers = L.markerClusterGroup({
		zoomToBoundsOnClick: true,
		showCoverageOnHover: false,
		maxClusterRadius: 80,
		spiderfyOnMaxZoom: true,
		removeOutsideVisibleBounds: false,
		iconCreateFunction: (cluster) => {
			const count = cluster.getChildCount();
			return getMapClusterIcon(container, count);
		},
	});

	map.addLayer(markers);

	locations.forEach((item) => {
		const lat = item.acf.location.lat;
		const lng = item.acf.location.lng;
		const marker = L.marker([lat, lng], { icon: mapMarker });

		const zoomToMarker = () => {
			map.setZoomAround([lat, lng], map.getZoom() + ZOOM_AMOUNT);
		};

		function dispatchOnMarkerClickEvent() {
			document.dispatchEvent(
				new CustomEvent('map:restaurant-selected', {
					detail: {
						mapId: map.id,
						data: item,
					},
				})
			);
		}

		marker.on('click', () => {
			dispatchOnMarkerClickEvent();
			zoomToMarker();
		});

		marker.on('add', () => {
			const markerElement = marker.getElement();
			if (markerElement) {
				markerElement.setAttribute(
					'aria-label',
					`Zoom to location ${item.id}`
				);
				markerElement.addEventListener('keydown', (e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						zoomToMarker();
					}
				});
			}
		});

		markers.addLayer(marker);
	});

	markers.refreshClusters();
	return markers;
}

function enableCtrlScrollZoom(map) {
	const overlay = document.getElementById('scroll-overlay');
	const mapContainer = map.getContainer();

	// Disable default scroll zoom
	map.scrollWheelZoom.disable();

	let ctrlDown = false;
	let overlayTimeout;

	// Track CTRL key state
	window.addEventListener('keydown', (e) => {
		if (e.key === 'Control') {
			ctrlDown = true;
			map.scrollWheelZoom.enable();
			overlay.classList.add('hidden');
		}
	});

	window.addEventListener('keyup', (e) => {
		if (e.key === 'Control') {
			ctrlDown = false;
			map.scrollWheelZoom.disable();
		}
	});

	// Handle scroll events on the map container
	mapContainer.addEventListener('wheel', (e) => {
		if (!ctrlDown) {
			// Show overlay when user tries to scroll without CTRL
			overlay.classList.remove('hidden');

			// Clear any existing timeout
			clearTimeout(overlayTimeout);

			// Hide overlay after 2 seconds of inactivity
			overlayTimeout = setTimeout(() => {
				overlay.classList.add('hidden');
			}, 2000);
		}
	});

	// Prevent scroll events on the overlay itself from bubbling to the map
	overlay.addEventListener('wheel', (e) => {
		e.stopPropagation();
	});
}

function enableTwoFingerDrag(map) {
	// Check if device supports touch
	const isTouchDevice =
		'ontouchstart' in window || navigator.maxTouchPoints > 0;

	if (!isTouchDevice) {
		// Enable normal dragging for desktop
		map.dragging.enable();
		return;
	}

	// Mobile-specific two-finger drag logic
	const handler = map.dragging;
	handler.enable();

	const mapContainer = map.getContainer();
	const touchOverlay = document.getElementById('touch-overlay');
	let touchCount = 0;
	let overlayTimeout;
	let isDragging = false;
	let hadTwoFingers = false; // Track if we previously had 2+ fingers

	mapContainer.addEventListener('touchstart', (e) => {
		touchCount = e.touches.length;
		isDragging = false;

		if (touchCount === 1) {
			// Disable dragging for single finger
			handler.disable();
			// Reset the two-finger flag on fresh single touch
			if (!hadTwoFingers) {
				hadTwoFingers = false;
			}
		} else if (touchCount >= 2) {
			// Enable dragging for two or more fingers
			handler.enable();
			hadTwoFingers = true; // Mark that we had two fingers

			// Hide touch overlay immediately when using two fingers
			if (touchOverlay) {
				touchOverlay.classList.add('hidden');
				clearTimeout(overlayTimeout);
			}
		}
	});

	mapContainer.addEventListener('touchmove', (e) => {
		// Only show overlay if user is dragging with one finger AND they didn't just have two fingers
		if (touchCount === 1 && !isDragging && !hadTwoFingers) {
			isDragging = true;

			// Show overlay when trying to drag with one finger
			if (touchOverlay) {
				touchOverlay.classList.remove('hidden');

				// Clear any existing timeout
				clearTimeout(overlayTimeout);

				// Hide overlay after 2 seconds
				overlayTimeout = setTimeout(() => {
					touchOverlay.classList.add('hidden');
				}, 2000);
			}
		}
	});

	mapContainer.addEventListener('touchend', (e) => {
		touchCount = e.touches.length;
		isDragging = false;

		if (touchCount < 2) {
			// Disable dragging when less than 2 fingers
			handler.disable();
		}

		// Reset the two-finger flag when all fingers are lifted
		if (touchCount === 0) {
			hadTwoFingers = false;
		}
	});

	// Prevent touch events on overlay from reaching the map
	if (touchOverlay) {
		touchOverlay.addEventListener('touchstart', (e) => {
			e.stopPropagation();
		});
	}
}

function initializeZoomButtons(map) {
	const buttons = document.querySelectorAll(
		'.locations-map__wrapper .locations-map-zoom-buttons .locations-map-zoom-button'
	);
	if (buttons.length === 0) return;

	let zoomInBtn = null;
	let zoomOutBtn = null;

	buttons.forEach((button) => {
		if (button.classList.contains('zoom-in')) {
			zoomInBtn = button;
		} else if (button.classList.contains('zoom-out')) {
			zoomOutBtn = button;
		}
	});

	if (zoomInBtn === null && zoomOutBtn === null) return;

	if (zoomInBtn) {
		zoomInBtn.addEventListener('click', () => {
			map.zoomIn();
		});
	}

	if (zoomOutBtn) {
		zoomOutBtn.addEventListener('click', () => {
			map.zoomOut();
		});
	}

	// Update button states based on zoom level
	const updateButtonStates = () => {
		const currentZoom = map.getZoom();
		const minZoom = map.getMinZoom();
		const maxZoom = map.getMaxZoom();

		if (zoomOutBtn) {
			zoomOutBtn.disabled = currentZoom <= minZoom;
		}

		if (zoomInBtn) {
			zoomInBtn.disabled = currentZoom >= maxZoom;
		}
	};

	// Initial state
	updateButtonStates();

	// Update on zoom changes
	map.on('zoomend', updateButtonStates);
}

function mapInit(container) {
	const mapElement = container.querySelector('.map');

	if (!mapElement) {
		console.error('No map element found in this container: ' + container);
		return;
	}

	const MAX_ZOOM = 18;
	const ZOOM_THRESHOLD = 8.5;

	const mapId = mapElement.id;
	const loader = container.querySelector('#map-loader');

	const map = L.map(mapElement, {
		zoomControl: false,
		maxZoom: MAX_ZOOM,
		zoomSnap: 0.1,
		touchZoom: true,
		doubleClickZoom: true,
		boxZoom: true,
		dragging: false,
		tapTolerance: 50,
		attributionControl: false,
	});

	map.id = mapId;

	enableTwoFingerDrag(map);
	enableCtrlScrollZoom(map);
	initializeZoomButtons(map);

	// --- Layers ---

	// GeoJSON border mask (visible at low zoom)
	const lithuaniaMask = L.geoJSON(lithuaniaGeoJson, {
		style: {
			color: 'var(--wp--preset--color--foreground, #000)',
			weight: 2,
			fillColor: 'var(--wp--preset--color--background, #fff)',
			fillOpacity: 1,
		},
	});

	// Clipped OSM tiles (visible at high zoom)
	const clippedOSM = L.TileLayer.boundaryCanvas(
		'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
		{
			boundary: lithuaniaGeoJson,
		}
	);

	const bounds = lithuaniaMask.getBounds();
	const center = bounds.getCenter();

	lithuaniaMask.addTo(map);

	// Swap layers on zoom
	map.on('zoomend', () => {
		const z = map.getZoom();

		if (z >= ZOOM_THRESHOLD) {
			if (!map.hasLayer(clippedOSM)) map.addLayer(clippedOSM);
			if (map.hasLayer(lithuaniaMask)) map.removeLayer(lithuaniaMask);
		} else {
			if (map.hasLayer(clippedOSM)) map.removeLayer(clippedOSM);
			if (!map.hasLayer(lithuaniaMask)) map.addLayer(lithuaniaMask);
		}
	});

	// Initial fit and min zoom
	const initialMinZoom = map.getBoundsZoom(bounds, false, [20, 20]);
	map.setMinZoom(initialMinZoom);
	map.setView(center, initialMinZoom);
	map.setMaxBounds(bounds);
	map.on('drag', () => map.panInsideBounds(bounds));

	// --- Markers ---
	getRestaurants(loader)
		.then((restaurants) => {
			initClusterMarkers(map, container, restaurants);
			findNearestRestaurant(map, restaurants);
		})
		.catch((error) => {
			console.error(error);
		});

	// Handle map resize on window resize (especially for aspect-ratio changes)
	let resizeTimeout;
	// window.addEventListener('resize', () => {
	// 	clearTimeout(resizeTimeout);
	// 	resizeTimeout = setTimeout(() => {
	// 		map.invalidateSize();
	// 		const dynamicMinZoom = map.getBoundsZoom(bounds, false, [20, 20]);
	// 		map.setMinZoom(dynamicMinZoom);
	// 		map.fitBounds(bounds, { padding: [20, 20] });
	// 	}, 200);
	// });
}

/**
 * Request the user's location and auto-select the nearest restaurant.
 * Falls back silently if geolocation is denied or unavailable.
 */
function findNearestRestaurant(map, restaurants) {
	console.log(
		navigator.geolocation
			? '[Map] Geolocation is available'
			: '[Map] Geolocation is NOT available'
	);
	if (!navigator.geolocation || !restaurants.length) return;

	navigator.geolocation.getCurrentPosition(
		(position) => {
			const userLat = position.coords.latitude;
			const userLng = position.coords.longitude;

			let nearest = null;
			let minDistance = Infinity;

			restaurants.forEach((restaurant) => {
				const lat = parseFloat(restaurant.acf?.location?.lat);
				const lng = parseFloat(restaurant.acf?.location?.lng);

				if (isNaN(lat) || isNaN(lng)) return;

				const distance = haversineDistance(userLat, userLng, lat, lng);

				if (distance < minDistance) {
					minDistance = distance;
					nearest = restaurant;
				}
			});

			if (!nearest) return;

			console.log(
				`[Map] Nearest restaurant: ${nearest.title?.rendered || nearest.id} (${minDistance.toFixed(1)} km)`
			);

			// Dispatch the same event the location-card listens to
			document.dispatchEvent(
				new CustomEvent('map:restaurant-selected', {
					detail: {
						mapId: map.id,
						data: nearest,
					},
				})
			);

			// Zoom the map to the nearest restaurant
			const lat = parseFloat(nearest.acf.location.lat);
			const lng = parseFloat(nearest.acf.location.lng);
			map.setView([lat, lng], 12, { animate: true });
		},
		(error) => {
			console.log(
				'[Map] Geolocation denied or unavailable:',
				error.message
			);
			// Silently fall back to default restaurant set in the editor
		},
		{
			enableHighAccuracy: false,
			timeout: 10000,
			maximumAge: 300000, // Cache location for 5 minutes
		}
	);
}

document.addEventListener('DOMContentLoaded', () => {
	document
		.querySelectorAll('.wp-block-cancan-locations-map')
		.forEach((container) => {
			mapInit(container);
		});
});
